/**
 * Who may open App info, and from where.
 *
 * It used to be a `ref` inside `SidebarFooter`, reachable only from the account
 * menu. Devin, 2026-09-14: *"can we not bring this app info into the main
 * settings?"* — and he is right: it is the screen you want when something is
 * wrong, and the account menu is not where anyone looks for that.
 *
 * The panel itself has to stay in the SHELL — build version, service-worker
 * state, storage quota, permissions and the resolved module bundles are all
 * shell concerns, and a module cannot see any of them. So the state moves here,
 * the drawer mounts once at app level, and anyone who wants to open it says so:
 *
 *   - the shell's own account menu calls `openAppInfo()`;
 *   - a MODULE, which lives in a different bundle and cannot import this, fires
 *     `window.dispatchEvent(new CustomEvent("metaship:open-app-info"))`.
 *
 * A custom event rather than something on `MetaShipGlobalState`: this is a
 * command, not state, and the global state object is persisted to localStorage
 * — an "open this panel" flag has no business surviving a reload.
 */

import { readonly, ref } from "vue";

export const OPEN_APP_INFO_EVENT = "metaship:open-app-info";

const open = ref(false);

export const appInfoOpen = readonly(open);

export function openAppInfo(): void {
  open.value = true;
}

export function closeAppInfo(): void {
  open.value = false;
}

let listening = false;

/** Called once by the shell on boot. Idempotent — a second call adds nothing. */
export function listenForAppInfoRequests(): void {
  if (listening || typeof window === "undefined") return;
  listening = true;
  window.addEventListener(OPEN_APP_INFO_EVENT, () => openAppInfo());
}
